import { signOut } from '@/lib/auth-client';
import { Button } from '@iq/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { LogOut } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Hook variant of the sign-out action. Returns `{ signOut, pending }` so
 * consumers (the dropdown item in the sidebar account footer, the
 * standalone `SignOutButton`, etc.) can wire the same Better Auth flow
 * without duplicating the query-invalidation and navigation side effects.
 */
export function useSignOut(): { signOut: () => Promise<void>; pending: boolean } {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);

  const run = useCallback(async () => {
    if (pending) return;
    setPending(true);
    try {
      await signOut();
      await queryClient.invalidateQueries({ queryKey: ['auth', 'session'] });
      await router.invalidate();
      navigate({ to: '/signin' });
    } finally {
      setPending(false);
    }
  }, [navigate, pending, queryClient, router]);

  return { signOut: run, pending };
}

export interface SignOutButtonProps {
  variant?: 'ghost' | 'outline' | 'default';
  size?: 'default' | 'sm' | 'icon';
}

export function SignOutButton({ variant = 'ghost', size = 'sm' }: SignOutButtonProps) {
  const { t } = useTranslation();
  const { signOut: runSignOut, pending } = useSignOut();

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={() => {
        void runSignOut();
      }}
      disabled={pending}
      data-testid="sign-out-button"
    >
      <LogOut className="mr-2 h-4 w-4" />
      {pending ? t('auth.signOut.pending') : t('auth.signOut.label')}
    </Button>
  );
}
